import React, { ReactNode } from 'react';
import PaginationNav from 'partials/products/PaginationNav';
import { productListViewed } from 'utils/metrics';
import CollectionListItem from 'partials/products/CollectionListItem';
import { Item } from 'types';
import SummaryCard from 'components/SummaryCard';
import SalesCalloutHTO from 'components/SalesCalloutHTO';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import SalesCalloutGroomsFree from 'components/SalesCalloutGroomsFree';

const PER_PAGE_DEFAULT = 24;
const PER_PAGE_BUNDLE = 50;

type BundleGroups = 'suit' | 'tux';

interface GroupedBundles {
  suit: Item[];
  tux: Item[];
}

interface Props {
  products: Item[];
  activeCategory: {
    name: string;
  };
  activeFilter: unknown;
  tuxOrSuits: boolean;
}

interface State {
  currentPage: number;
  perPage: number;
}

class CollectionList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      currentPage: 1,
      perPage: PER_PAGE_DEFAULT,
    };
    this.incrementPage = this.incrementPage.bind(this);
    this.decrementPage = this.decrementPage.bind(this);
  }

  componentDidMount() {
    this.trackProductListViewed();
    this.setItemsPerPage();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.products !== this.props.products) {
      this.setState({
        currentPage: 1,
      });
    }
    this.setItemsPerPage();
  }

  setItemsPerPage = () => {
    if (this.props.tuxOrSuits && this.state.perPage !== PER_PAGE_BUNDLE) {
      this.setState({ perPage: PER_PAGE_BUNDLE });
    }

    if (!this.props.tuxOrSuits && this.state.perPage === PER_PAGE_BUNDLE) {
      this.setState({ perPage: PER_PAGE_DEFAULT });
    }
  };

  incrementPage = () => {
    this.scrollToTop();
    this.setState(
      (state) => ({ currentPage: state.currentPage + 1 }),
      () => {
        this.trackProductListViewed();
      },
    );
  };

  decrementPage = () => {
    this.scrollToTop();
    this.setState(
      (state) => ({ currentPage: state.currentPage - 1 }),
      () => {
        this.trackProductListViewed();
      },
    );
  };

  scrollToTop = () => {
    document.getElementById('collection-nav')?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  trackProductListViewed = () => productListViewed(this.getCurrentPageProducts(), this.props.activeCategory.name);

  getCurrentPageProducts = () =>
    this.props.products.slice(
      this.state.currentPage * this.state.perPage - this.state.perPage,
      this.state.currentPage * this.state.perPage,
    );

  groupedBundles = () =>
    this.getCurrentPageProducts().reduce(
      (acc: GroupedBundles, val: Item) => {
        const isSuit = val.type === 'suit';
        const isTux = val.type === 'tuxedo';

        if (isSuit) {
          acc['suit'] = [...acc['suit'], val];
        }

        if (isTux) {
          acc['tux'] = [...acc['tux'], val];
        }

        return acc;
      },
      { suit: [], tux: [] },
    );

  renderCombinedProductsAndCallouts = (currentPageProducts: GroupedBundles) => {
    const groups: BundleGroups[] = ['suit', 'tux'];

    let callouts = [
      <div className="collection-page-sales-callouts col-span-6 flex md:col-span-4" key="hto-sales-callout">
        <div className="flex flex-1 overflow-hidden aspect-ratio-5by7">
          <SalesCalloutHTO ctaPageName="collection" />
        </div>
      </div>,
      <div className="collection-page-sales-callouts col-span-6 flex md:col-span-4" key="swatches-sales-callout">
        <div className="flex flex-1 overflow-hidden aspect-ratio-5by7"></div>
        <SalesCalloutGroomsFree ctaPageName="collection" />
      </div>,
      <div className="collection-page-sales-callouts col-span-6 flex md:col-span-4" key="groomsfree-sales-callout">
        <div className="flex flex-1 overflow-hidden aspect-ratio-5by7">
          <SalesCalloutSwatches ctaPageName="collection" />
        </div>
      </div>,
    ];

    const summaryCards = {
      suit: (
        <div className="col-span-12 flex lg:col-span-4 lg:col-start-1" key="suit-summary-card" id="suits">
          <div className="flex flex-1 overflow-hidden lg:aspect-ratio-5by7">
            <SummaryCard title="Suits">
              Sleek, stylish and comfortable. Choose your suit color and accessories to build out the perfect look. Stand out in your suit
              at a formal wedding or a more casual party.
            </SummaryCard>
          </div>
        </div>
      ),
      tux: (
        <div className="col-span-12 flex lg:col-span-4 lg:col-start-1" key="tux-summary-card" id="tuxedos">
          <div className="flex flex-1 overflow-hidden lg:aspect-ratio-5by7">
            <SummaryCard title="Tuxedos">
              Classic and confident tuxedos that will turn heads. Style your dream look for you and your wedding party.
            </SummaryCard>
          </div>
        </div>
      ),
    };

    const summaryWithBundles = groups.reduce((acc: ReactNode[], val: BundleGroups) => {
      if (currentPageProducts[val].length > 0) {
        acc.push(summaryCards[val]);

        currentPageProducts[val].map((p: Item, index) => {
          // add sales callout every 5th item
          if ((index + 1) % 5 === 0) {
            const callout = callouts.shift();
            if (callout) {
              acc.push(callout);
            }
          }

          acc.push(<CollectionListItem key={`${p.category}-${p.id}`} product={p} tuxOrSuits={this.props.tuxOrSuits} />);
        });
      }

      return acc;
    }, []);

    return summaryWithBundles;
  };

  renderProducts = () => {
    const currentPageProducts = this.getCurrentPageProducts().map((p) => (
      <CollectionListItem key={`${p.category}-${p.id}`} product={p} tuxOrSuits={this.props.tuxOrSuits} />
    ));

    if (this.props.activeCategory.name === 'preconfigured') {
      // bundle pages
      if (!this.props.activeFilter) {
        const groupedBundles = this.groupedBundles();
        return this.renderCombinedProductsAndCallouts(groupedBundles);
      } else {
        return currentPageProducts;
      }
    } else {
      // product pages
      return currentPageProducts;
    }
  };

  render() {
    return (
      <>
        <div id="item-list" className="relative">
          <div
            id="collection-nav"
            className="
              absolute
              right-0
              translate-y-[-150%]
              transform
              sm:translate-y-[-200%]
            "
          >
            <PaginationNav
              currentPage={this.state.currentPage}
              perPage={this.state.perPage}
              items={this.props.products}
              decrementPage={this.decrementPage}
              incrementPage={this.incrementPage}
            />
          </div>
          <div className="row mb-32 gap-y-16 sm:gap-y-32">{this.renderProducts()}</div>
          <PaginationNav
            currentPage={this.state.currentPage}
            perPage={this.state.perPage}
            items={this.props.products}
            decrementPage={this.decrementPage}
            incrementPage={this.incrementPage}
          />
        </div>
      </>
    );
  }
}

export default CollectionList;
